import { reactive } from 'vue';
import { useMutation } from '@/helpers/composables/useApollo';

import {
	UpdateProfileDocument,
	UpdateProfileMutation,
	UpdateProfileMutationVariables,
} from '@/features/user-profile/graphql/mutations/UpdateProfile.mutation';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useI18n } from '@/helpers/composables/useI18n';

export function useUserProfileEdit() {
	const { tc } = useI18n();

	const languageStore = useLanguageStore();
	const userStore = useUserStore();

	const { mutate, loading, onError, onDone } = useMutation<UpdateProfileMutation, UpdateProfileMutationVariables>(
		UpdateProfileDocument,
		{ refetchQueries: ['GetUserStats', 'GetProfileByUrl'] }
	);

	const profileData = reactive({
		firstName: userStore.profileInfo?.firstName,
		lastName: userStore.profileInfo?.lastName,
		country: !userStore.profileInfo?.country ? languageStore.country : userStore.profileInfo?.country,
		city: userStore.profileInfo?.city,
		bio: userStore.profileInfo?.bio,
		avatarBase64: null as string | null,
		website: userStore.profileInfo?.website?.url,
		altWebsite: userStore.profileInfo?.altWebsite?.url,
		instagram: userStore.profileInfo?.instagram?.url,
		imdb: userStore.profileInfo?.imdb?.url,
		x: userStore.profileInfo?.x?.url,
		tiktok: userStore.profileInfo?.tiktok?.url,
		linkedin: userStore.profileInfo?.linkedin?.url,
		facebook: userStore.profileInfo?.facebook?.url,
	});

	const countries = [
		{ item: tc('WEBAPP_COUNTRY_FR'), value: 'FR' },
		{ item: tc('WEBAPP_COUNTRY_DE'), value: 'DE' },
		{ item: tc('WEBAPP_COUNTRY_IT'), value: 'IT' },
		{ item: tc('WEBAPP_COUNTRY_PT'), value: 'PT' },
		{ item: tc('WEBAPP_COUNTRY_ES'), value: 'ES' },
		{ item: tc('WEBAPP_COUNTRY_TR'), value: 'TR' },
		{ item: tc('WEBAPP_COUNTRY_GB'), value: 'GB' },
		{ item: tc('WEBAPP_COUNTRY_US'), value: 'US' },
	];

	function submitProfile() {
		const externalLinksArray = [
			profileData.instagram,
			profileData.imdb,
			profileData.x,
			profileData.tiktok,
			profileData.linkedin,
			profileData.facebook,
		].filter(link => !!link);
		const websitesArray = [profileData.website, profileData.altWebsite].filter(link => !!link);

		mutate({
			input: {
				firstName: profileData.firstName,
				lastName: profileData.lastName,
				avatarBase64: profileData.avatarBase64,
				country: profileData.country,
				location: profileData.city,
				bio: profileData.bio,
				externalUrls: [...websitesArray, ...externalLinksArray] as string[],
			},
		});
	}

	return {
		profileData,
		submitProfile,
		countries,
		loading,
		onError,
		onDone,
	};
}
